import React, { useState, useMemo } from "react";
import SearchBarWithFilter from "../../components/SearchBarWithFilter";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { formatName, getCurrentStatus } from "../../util/AppUtils.jsx";
import { SelectRowButton } from "../../common/Buttons";
import { SortTableData } from "../../common/AppTable/SortTableData.js";

/**
 * SelectRevisionModal - Modal for selecting a revision from a list
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {function} props.onClose - Callback when modal is closed
 * @param {Array} props.revisionData - Array of revision objects
 * @param {Object} props.currentPartInfo - Information about the current part
 * @param {function} props.onRevisionSelect - Callback when a revision is selected
 */
const SelectRevisionModal = ({
  isOpen,
  onClose,
  revisionData = [],
  currentPartInfo = {},
  onRevisionSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("all_columns");
  const [selectedColumns, setSelectedColumns] = useState({
    rev: true,
    date_released: true,
    revision_details: true,
    submitted_requestor: true,
    approver: true,
    references: true,
    status: true,
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  // Helper function to get cell value for a column
  const getCellValue = (rev, columnId) => {
    const requiredBy = "required_by" in rev ? rev.required_by : [];

    switch (columnId) {
      case "rev":
        return rev.rev || "";
      case "date_released":
        return rev.date_released
          ? new Date(rev.date_released).toISOString().split("T")[0]
          : "";
      case "revision_details":
        return rev.revision_details || "";
      case "submitted_requestor":
        if (
          rev.status_changes?.find((sc) => sc.is_latest)?.status_changed_to ===
          "MAINTAINED OUTSIDE OF SYSTEM"
        ) {
          return "MAINTAINED OUTSIDE OF SYSTEM";
        }
        return requiredBy
          .filter((rb) => rb.purpose?.toLowerCase() === "submit")
          .map((rb) => {
            const name = formatName(rev, rb.user);
            return !rb.is_open
              ? `Submitted by ${name}`
              : `Pending Submission from ${name}`;
          })
          .join(" ");
      case "approver":
        if (
          rev.status_changes?.find((sc) => sc.is_latest)?.status_changed_to ===
          "MAINTAINED OUTSIDE OF SYSTEM"
        ) {
          return "MAINTAINED OUTSIDE OF SYSTEM";
        }
        return requiredBy
          .filter((rb) => rb.purpose?.toLowerCase() === "approve")
          .map((rb) => {
            const name = formatName(rev, rb.user);
            return !rb.is_open
              ? `Approved by ${name}`
              : `Pending Approval from ${name}`;
          })
          .join(" ");
      case "references":
        return rev.reference || "";
      case "status":
        return getCurrentStatus(rev);
      default:
        return "";
    }
  };

  // Filter revision data based on search query and selected columns
  const filteredRevisionData = useMemo(() => {
    let data = revisionData;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const columnsToSearch =
        searchType === "all_columns"
          ? [
              "rev",
              "date_released",
              "revision_details",
              "submitted_requestor",
              "approver",
              "references",
              "status",
            ]
          : Object.keys(selectedColumns).filter((key) => selectedColumns[key]);

      data = data.filter((rev) => {
        return columnsToSearch.some((columnId) => {
          const cellValue = getCellValue(rev, columnId);
          return String(cellValue).toLowerCase().includes(query);
        });
      });
    }

    // Apply sorting - matches AppTable logic
    if (sortConfig.key !== null && sortConfig.direction !== null) {
      if (
        sortConfig.key.length > 0 &&
        sortConfig.direction.length > 0 &&
        sortConfig.direction !== "DEF"
      ) {
        // Map display column names to data keys
        const columnKeyMap = {
          rev: "rev",
          date_released: "date_released",
          revision_details: "revision_details",
          submitted_requestor: "submitted_requestor",
          approver: "approver",
          references: "reference",
          status: "status",
        };

        const actualKey = columnKeyMap[sortConfig.key] || sortConfig.key;

        // For columns that need computed values, we'll sort by the computed value
        if (
          actualKey === "submitted_requestor" ||
          actualKey === "approver" ||
          actualKey === "status"
        ) {
          data = [...data].sort((a, b) => {
            const aValue = getCellValue(a, sortConfig.key);
            const bValue = getCellValue(b, sortConfig.key);
            const direction = sortConfig.direction === "ASC" ? 1 : -1;
            return direction * String(aValue).localeCompare(String(bValue));
          });
        } else {
          // Use SortTableData for standard columns
          data = SortTableData(data, sortConfig.direction, actualKey);
        }
      }
    } else {
      // Default sort by rev descending (newest first) when no sort is applied
      data = [...data].sort((a, b) => b.rev.localeCompare(a.rev));
    }

    return data;
  }, [revisionData, searchQuery, searchType, selectedColumns, sortConfig]);

  // Handle column header click for sorting - matches AppTable logic exactly
  const handleSort = (key) => {
    let direction = "";
    if (sortConfig.key !== key) {
      direction = "ASC";
    } else if (
      sortConfig.direction === null ||
      sortConfig.direction === "" ||
      sortConfig.direction === "DEF"
    ) {
      direction = "ASC";
    } else if (sortConfig.direction === "ASC") {
      direction = "DESC";
    } else if (sortConfig.direction === "DESC") {
      direction = "DEF";
    }
    setSortConfig({ key, direction });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-content">
          <h3 className="modal-title-center-primary">Select Revision</h3>
          <button
            className="close-modal-top-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>

          <div className="search-container">
            <SearchBarWithFilter
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              columnOptions={[
                { id: "rev", label: "Rev" },
                { id: "date_released", label: "Date Released" },
                { id: "revision_details", label: "Revision Details" },
                {
                  id: "submitted_requestor",
                  label: "Submitter /Requestor",
                },
                { id: "approver", label: "Approver" },
                { id: "references", label: "References" },
                { id: "status", label: "Status" },
              ]}
              searchType={searchType}
              setSearchType={setSearchType}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
            />
          </div>

          <div className="sticky-table-container">
            <table className="table sticky-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("rev")}
                    className="sortable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      className="header-content"
                      style={{
                        paddingTop: "10px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span>Rev</span>
                      <div className="sort-icons" style={{ fontSize: "8px" }}>
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "DESC" &&
                              sortConfig.key === "rev"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▲"}
                        </span>
                        {"\n"}
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "ASC" &&
                              sortConfig.key === "rev"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▼"}
                        </span>
                      </div>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("date_released")}
                    className="sortable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      className="header-content"
                      style={{
                        paddingTop: "10px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span>Date Released</span>
                      <div className="sort-icons" style={{ fontSize: "8px" }}>
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "DESC" &&
                              sortConfig.key === "date_released"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▲"}
                        </span>
                        {"\n"}
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "ASC" &&
                              sortConfig.key === "date_released"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▼"}
                        </span>
                      </div>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("revision_details")}
                    className="sortable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      className="header-content"
                      style={{
                        paddingTop: "10px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span>Revision Details</span>
                      <div className="sort-icons" style={{ fontSize: "8px" }}>
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "DESC" &&
                              sortConfig.key === "revision_details"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▲"}
                        </span>
                        {"\n"}
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "ASC" &&
                              sortConfig.key === "revision_details"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▼"}
                        </span>
                      </div>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("submitted_requestor")}
                    className="sortable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      className="header-content"
                      style={{
                        paddingTop: "10px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span>Submitter / Requestor</span>
                      <div className="sort-icons" style={{ fontSize: "8px" }}>
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "DESC" &&
                              sortConfig.key === "submitted_requestor"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▲"}
                        </span>
                        {"\n"}
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "ASC" &&
                              sortConfig.key === "submitted_requestor"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▼"}
                        </span>
                      </div>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("approver")}
                    className="sortable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      className="header-content"
                      style={{
                        paddingTop: "10px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span>Approver</span>
                      <div className="sort-icons" style={{ fontSize: "8px" }}>
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "DESC" &&
                              sortConfig.key === "approver"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▲"}
                        </span>
                        {"\n"}
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "ASC" &&
                              sortConfig.key === "approver"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▼"}
                        </span>
                      </div>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("references")}
                    className="sortable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      className="header-content"
                      style={{
                        paddingTop: "10px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span>References</span>
                      <div className="sort-icons" style={{ fontSize: "8px" }}>
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "DESC" &&
                              sortConfig.key === "references"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▲"}
                        </span>
                        {"\n"}
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "ASC" &&
                              sortConfig.key === "references"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▼"}
                        </span>
                      </div>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    className="sortable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      className="header-content"
                      style={{
                        paddingTop: "10px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span>Status</span>
                      <div className="sort-icons" style={{ fontSize: "8px" }}>
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "DESC" &&
                              sortConfig.key === "status"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▲"}
                        </span>
                        {"\n"}
                        <span
                          style={{
                            opacity:
                              sortConfig.direction === "ASC" &&
                              sortConfig.key === "status"
                                ? 0.5
                                : 1,
                          }}
                        >
                          {"▼"}
                        </span>
                      </div>
                    </div>
                  </th>
                  <th className="no-sort">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRevisionData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No revisions found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredRevisionData.map((rev, index) => {
                    const requiredBy =
                      "required_by" in rev ? rev.required_by : [];
                    const currentStatus = getCurrentStatus(rev);

                    return (
                      <tr key={index}>
                        <td>{rev.rev || "-"}</td>
                        <td>
                          {rev.date_released
                            ? new Date(rev.date_released)
                                .toISOString()
                                .split("T")[0]
                            : "-"}
                        </td>

                        <td>{rev.revision_details || "-"}</td>

                        {/* Submitter / Requestor */}
                        <td>
                          {rev.status_changes?.find((sc) => sc.is_latest)
                            ?.status_changed_to ===
                          "MAINTAINED OUTSIDE OF SYSTEM" ? (
                            <div>MAINTAINED OUTSIDE OF SYSTEM</div>
                          ) : (
                            requiredBy
                              .filter(
                                (rb) => rb.purpose?.toLowerCase() === "submit"
                              )
                              .map((rb, i) => {
                                const name = formatName(rev, rb.user);
                                return (
                                  <div key={i}>
                                    {!rb.is_open
                                      ? `Submitted by ${name}`
                                      : `Pending Submission from ${name}`}
                                  </div>
                                );
                              })
                          )}
                        </td>

                        {/* Approver */}
                        <td>
                          {rev.status_changes?.find((sc) => sc.is_latest)
                            ?.status_changed_to ===
                          "MAINTAINED OUTSIDE OF SYSTEM" ? (
                            <div>MAINTAINED OUTSIDE OF SYSTEM</div>
                          ) : (
                            requiredBy
                              .filter(
                                (rb) => rb.purpose?.toLowerCase() === "approve"
                              )
                              .map((rb, i) => {
                                const name = formatName(rev, rb.user);
                                return (
                                  <div key={i}>
                                    {!rb.is_open
                                      ? `Approved by ${name}`
                                      : `Pending Approval from ${name}`}
                                  </div>
                                );
                              })
                          )}
                        </td>

                        <td>{rev.reference || "-"}</td>
                        <td>{currentStatus}</td>
                        <td>
                          {!rev.is_maintained_outside_system && (
                            <SelectRowButton
                              onClick={() => {
                                if (onRevisionSelect) {
                                  onRevisionSelect({
                                    procedureRevisionId:
                                      rev.procedure_revision_id,
                                    revision: rev.rev,
                                    status: currentStatus,
                                    partId: rev.part_ref,
                                  });
                                }
                              }}
                              ariaLabel={`Select revision ${rev.rev}`}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectRevisionModal;
